import React, { useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Table,
  Badge,
  Button,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  CompanyDocumentDto,
  PartnerDocumentDto,
  IndividualDocumentDto,
  DocumentOwnerType,
  CompanyDocumentType,
  PartnerDocumentType,
  IndividualDocumentType,
  getDocumentTypeLabel,
  getFileIcon,
  formatFileSize,
  formatDateTime,
  formatDate,
  getDocumentSphereLabel,
  getDocumentSphereColor,
} from '../../../../types/documents';

interface DocumentListProps {
  ownerType: DocumentOwnerType;
  documents: (CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto)[];
  onDownload: (document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => void;
  onPreview: (document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => void;
  onDocumentSelect?: (document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => void;
  onReplace?: (document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => void;
  onDelete?: (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    reason: string
  ) => void;
  onRestore?: (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    reason: string
  ) => void;
  onDocumentTypeChange?: (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    newType: CompanyDocumentType | PartnerDocumentType | IndividualDocumentType
  ) => void;
  onFileNameUpdate?: (
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto,
    newFileName: string
  ) => void;
  includeDeleted?: boolean;
  isAdmin?: boolean;
  processingDocumentId?: string | null;
}

const DocumentList: React.FC<DocumentListProps> = ({
  ownerType,
  documents,
  onDownload,
  onPreview,
  onDocumentSelect,
  onReplace,
  onDelete,
  onRestore,
  onDocumentTypeChange,
  onFileNameUpdate,
  includeDeleted = false,
  isAdmin = false,
  processingDocumentId = null,
}) => {
  const { t } = useTranslation();

  // Helper function to calculate days remaining until expiration
  const calculateDaysRemaining = (expirationDate: string | undefined): { days: number; label: string; color: string } | null => {
    if (!expirationDate) return null;

    const expDate = new Date(expirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);

    const diffTime = expDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return {
        days: Math.abs(diffDays),
        label: `Expirado há ${Math.abs(diffDays)} ${Math.abs(diffDays) === 1 ? 'dia' : 'dias'}`,
        color: 'danger',
      };
    } else if (diffDays === 0) {
      return {
        days: 0,
        label: 'Expira hoje',
        color: 'danger',
      };
    } else if (diffDays <= 30) {
      return {
        days: diffDays,
        label: `${diffDays} ${diffDays === 1 ? 'dia' : 'dias'} restantes`,
        color: 'warning',
      };
    } else {
      return {
        days: diffDays,
        label: `${diffDays} ${diffDays === 1 ? 'dia' : 'dias'} restantes`,
        color: 'success',
      };
    }
  };

  // Helper function to normalize document type to integer
  // Handles both integer (correct) and string (legacy API) values
  const normalizeDocumentType = useCallback((docType: any): number => {
    if (typeof docType === 'number') {
      return docType;
    }
    // If it's a string, try to find the matching enum value
    if (typeof docType === 'string') {
      if (ownerType === DocumentOwnerType.Company) {
        const enumValue = CompanyDocumentType[docType as keyof typeof CompanyDocumentType];
        return typeof enumValue === 'number' ? enumValue : 99; // Default to Other
      } else if (ownerType === DocumentOwnerType.Partner) {
        const enumValue = PartnerDocumentType[docType as keyof typeof PartnerDocumentType];
        return typeof enumValue === 'number' ? enumValue : 99; // Default to Other
      } else if (ownerType === DocumentOwnerType.Individual) {
        const enumValue = IndividualDocumentType[docType as keyof typeof IndividualDocumentType];
        return typeof enumValue === 'number' ? enumValue : 99; // Default to Other
      }
    }
    return 99; // Default to Other if we can't parse it
  }, [ownerType]);

  // Helper function to get all document type options for the owner type
  const getDocumentTypeOptions = (): { value: number; label: string }[] => {
    if (ownerType === DocumentOwnerType.Company) {
      return [
        { value: CompanyDocumentType.ECNPJ, label: 'e-CNPJ' },
        { value: CompanyDocumentType.ContratoSocial, label: 'Contrato Social' },
        { value: CompanyDocumentType.CartaoCNPJ, label: 'Cartão CNPJ' },
        { value: CompanyDocumentType.Alvara, label: 'Alvará de Funcionamento' },
        { value: CompanyDocumentType.InscricaoEstadual, label: 'Inscrição Estadual' },
        { value: CompanyDocumentType.InscricaoMunicipal, label: 'Inscrição Municipal' },
        { value: CompanyDocumentType.DARFDAS, label: 'DARF/DAS' },
        { value: CompanyDocumentType.CertidaoNegativa, label: 'Certidão Negativa' },
        { value: CompanyDocumentType.ContratoLocacao, label: 'Contrato de Locação' },
        { value: CompanyDocumentType.ComprovanteEndereco, label: 'Comprovante de Endereço' },
        // New document types with sphere and validity
        { value: CompanyDocumentType.CertidaoConjuntaReceitaFederalPGFN, label: 'Certidão Conjunta Receita Federal/PGFN' },
        { value: CompanyDocumentType.CertificadoRegularidadeFGTS, label: 'Certificado Regularidade FGTS' },
        { value: CompanyDocumentType.CertidaoNegativaDebitosTrabalhistasCNDT, label: 'Certidão Negativa Débitos Trabalhistas (CNDT)' },
        { value: CompanyDocumentType.CertidaoNegativaDebitosEstaduais, label: 'Certidão Negativa Débitos Estaduais' },
        { value: CompanyDocumentType.CertidaoRegularidadeICMS, label: 'Certidão Regularidade ICMS' },
        { value: CompanyDocumentType.CertidaoNegativaDebitosMunicipais, label: 'Certidão Negativa Débitos Municipais' },
        { value: CompanyDocumentType.CertidaoFalenciaConcordataRecuperacaoJudicial, label: 'Certidão Falência/Concordata/Recuperação Judicial' },
        { value: CompanyDocumentType.CertificadoEntidadeClasse, label: 'Certificado Entidade de Classe' },
        { value: CompanyDocumentType.CertidaoRegularidadeINSS, label: 'Certidão Regularidade INSS' },
        { value: CompanyDocumentType.AlvaraFuncionamento, label: 'Alvará de Funcionamento' },
        { value: CompanyDocumentType.AlvaraVigilanciaSanitaria, label: 'Alvará Vigilância Sanitária' },
        { value: CompanyDocumentType.LicencaCorpoBombeiros, label: 'Licença Corpo de Bombeiros (AVCB)' },
        { value: CompanyDocumentType.Other, label: 'Outros Documentos' },
      ];
    } else if (ownerType === DocumentOwnerType.Partner) {
      return [
        { value: PartnerDocumentType.ECPF, label: 'e-CPF' },
        { value: PartnerDocumentType.RG, label: 'RG' },
        { value: PartnerDocumentType.CPF, label: 'CPF' },
        { value: PartnerDocumentType.CNH, label: 'CNH' },
        { value: PartnerDocumentType.ComprovanteResidencia, label: 'Comprovante de Residência' },
        { value: PartnerDocumentType.EstadoCivil, label: 'Estado Civil' },
        { value: PartnerDocumentType.CertidaoCasamento, label: 'Certidão de Casamento' },
        { value: PartnerDocumentType.CertidaoDivorcio, label: 'Certidão de Divórcio' },
        { value: PartnerDocumentType.TituloEleitor, label: 'Título de Eleitor' },
        { value: PartnerDocumentType.Procuracao, label: 'Procuração' },
        { value: PartnerDocumentType.Other, label: 'Outros Documentos' },
      ];
    } else {
      return [
        { value: IndividualDocumentType.ECPF, label: 'e-CPF' },
        { value: IndividualDocumentType.RG, label: 'RG' },
        { value: IndividualDocumentType.CNH, label: 'CNH' },
        { value: IndividualDocumentType.ProofOfAddress, label: 'Comprovante de Endereço' },
        { value: IndividualDocumentType.BirthCertificate, label: 'Certidão de Nascimento' },
        { value: IndividualDocumentType.MarriageCertificate, label: 'Certidão de Casamento' },
        { value: IndividualDocumentType.DivorceCertificate, label: 'Certidão de Divórcio' },
        { value: IndividualDocumentType.CPFCard, label: 'Cartão CPF' },
        { value: IndividualDocumentType.VoterRegistration, label: 'Título de Eleitor' },
        { value: IndividualDocumentType.WorkCard, label: 'Carteira de Trabalho' },
        { value: IndividualDocumentType.Proposta, label: 'Proposta' },
        { value: IndividualDocumentType.ContratoServico, label: 'Contrato de Prestação de Serviços' },
        { value: IndividualDocumentType.AtaReuniao, label: 'Ata de Reunião' },
        { value: IndividualDocumentType.Other, label: 'Outros Documentos' },
      ];
    }
  };

  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto | null;
  }>({ isOpen: false, document: null });
  const [deleteReason, setDeleteReason] = useState('');

  const [restoreModal, setRestoreModal] = useState<{
    isOpen: boolean;
    document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto | null;
  }>({ isOpen: false, document: null });
  const [restoreReason, setRestoreReason] = useState('');

  const [editingFileName, setEditingFileName] = useState<string | null>(null);
  const [editedFileName, setEditedFileName] = useState<string>('');

  // Sorting state - default sort by Sphere, then by Vencimento (days remaining)
  type SortField = 'sphere' | 'startDate' | 'expirationDate' | 'vencimento' | 'fileName' | 'tipo' | 'tamanho' | 'dataUpload';
  const [sortField, setSortField] = useState<SortField>('sphere');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Sort documents
  const sortedDocuments = React.useMemo(() => {
    if (!documents || documents.length === 0) return documents;

    return [...documents].sort((a, b) => {
      const multiplier = sortDirection === 'asc' ? 1 : -1;

      // Helper to get sphere value for company documents
      const getSphere = (doc: any) => {
        if (ownerType === DocumentOwnerType.Company) {
          return (doc as CompanyDocumentDto).sphere || 0;
        }
        return 0;
      };

      // Helper to get days remaining
      const getDaysRemaining = (doc: any) => {
        const daysData = calculateDaysRemaining(doc.expirationDate);
        if (!daysData) return Number.MAX_SAFE_INTEGER; // Permanent documents go last
        return daysData.days * (daysData.color === 'danger' && daysData.days > 0 ? -1 : 1); // Expired/expiring today first
      };

      let comparison = 0;

      switch (sortField) {
        case 'sphere':
          const sphereA = getSphere(a);
          const sphereB = getSphere(b);
          comparison = sphereA - sphereB;
          // Secondary sort by vencimento if sphere is the same
          if (comparison === 0) {
            const daysA = getDaysRemaining(a);
            const daysB = getDaysRemaining(b);
            comparison = daysA - daysB;
          }
          break;

        case 'vencimento':
          const daysA = getDaysRemaining(a);
          const daysB = getDaysRemaining(b);
          comparison = daysA - daysB;
          break;

        case 'startDate':
          const startA = (ownerType === DocumentOwnerType.Company && (a as CompanyDocumentDto).startDate)
            ? new Date((a as CompanyDocumentDto).startDate!).getTime()
            : 0;
          const startB = (ownerType === DocumentOwnerType.Company && (b as CompanyDocumentDto).startDate)
            ? new Date((b as CompanyDocumentDto).startDate!).getTime()
            : 0;
          comparison = startA - startB;
          break;

        case 'expirationDate':
          const expA = a.expirationDate ? new Date(a.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
          const expB = b.expirationDate ? new Date(b.expirationDate).getTime() : Number.MAX_SAFE_INTEGER;
          comparison = expA - expB;
          break;

        case 'fileName':
          comparison = a.originalFileName.localeCompare(b.originalFileName);
          break;

        case 'tipo':
          const typeA = normalizeDocumentType(a.documentType);
          const typeB = normalizeDocumentType(b.documentType);
          comparison = typeA - typeB;
          break;

        case 'tamanho':
          comparison = a.fileSize - b.fileSize;
          break;

        case 'dataUpload':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;

        default:
          comparison = 0;
      }

      return comparison * multiplier;
    });
  }, [documents, sortField, sortDirection, ownerType, normalizeDocumentType]);

  // Handle column header click for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleDeleteClick = (document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => {
    setDeleteModal({ isOpen: true, document });
    setDeleteReason('');
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.document && onDelete) {
      onDelete(deleteModal.document, deleteReason);
      setDeleteModal({ isOpen: false, document: null });
      setDeleteReason('');
    }
  };

  const handleRestoreClick = (document: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => {
    setRestoreModal({ isOpen: true, document });
    setRestoreReason('');
  };

  const handleRestoreConfirm = () => {
    if (restoreModal.document && onRestore) {
      onRestore(restoreModal.document, restoreReason);
      setRestoreModal({ isOpen: false, document: null });
      setRestoreReason('');
    }
  };

  const handleFileNameClick = (doc: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFileNameUpdate && !doc.isDeleted) {
      setEditingFileName(doc.id);
      setEditedFileName(doc.originalFileName);
    }
  };

  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedFileName(e.target.value);
  };

  const handleFileNameBlur = (doc: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto) => {
    if (editedFileName.trim() && editedFileName !== doc.originalFileName && onFileNameUpdate) {
      onFileNameUpdate(doc, editedFileName.trim());
    }
    setEditingFileName(null);
    setEditedFileName('');
  };

  const handleFileNameKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    doc: CompanyDocumentDto | PartnerDocumentDto | IndividualDocumentDto
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editedFileName.trim() && editedFileName !== doc.originalFileName && onFileNameUpdate) {
        onFileNameUpdate(doc, editedFileName.trim());
      }
      setEditingFileName(null);
      setEditedFileName('');
    } else if (e.key === 'Escape') {
      setEditingFileName(null);
      setEditedFileName('');
    }
  };

  if (documents.length === 0) {
    return (
      <Card className="border">
        <CardBody className="text-center py-5">
          <i className="ri-file-list-line display-4 text-muted"></i>
          <p className="text-muted mt-3 mb-0">
            {t('Nenhum documento encontrado')}
          </p>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card className="border">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <i className="ri-file-list-3-line me-2"></i>
            {t('Arquivos')} ({documents.length})
          </h5>
        </CardHeader>
        <CardBody className="p-3">
          <div className="table-responsive" style={{ minHeight: '300px' }}>
            <Table className="table-hover align-middle mb-0 table-sm">
              <thead className="table-light">
                <tr>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('fileName')}>
                    {t('Documento')}{renderSortIndicator('fileName')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('tipo')}>
                    {t('Tipo')}{renderSortIndicator('tipo')}
                  </th>
                  {ownerType === DocumentOwnerType.Company && (
                    <>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('sphere')}>
                        {t('Esfera')}{renderSortIndicator('sphere')}
                      </th>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('startDate')}>
                        {t('Data Inicio')}{renderSortIndicator('startDate')}
                      </th>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('expirationDate')}>
                        {t('Data Expiracao')}{renderSortIndicator('expirationDate')}
                      </th>
                      <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('vencimento')}>
                        {t('Vencimento')}{renderSortIndicator('vencimento')}
                      </th>
                    </>
                  )}
                  <th>{t('Status')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('tamanho')}>
                    {t('Tamanho')}{renderSortIndicator('tamanho')}
                  </th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('dataUpload')}>
                    {t('Data de Upload')}{renderSortIndicator('dataUpload')}
                  </th>
                  <th className="text-end">{t('Ações')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedDocuments.map((doc) => {
                  const isProcessing = processingDocumentId === doc.id;
                  return (
                  <tr
                    key={doc.id}
                    className={`${doc.isDeleted ? 'table-secondary' : ''} ${isProcessing ? 'table-warning opacity-75' : ''}`}
                    onClick={(e) => {
                      // Only trigger if clicking on the row itself, not on buttons/inputs
                      if (isProcessing) return;
                      const target = e.target as HTMLElement;
                      if (!target.closest('button') && !target.closest('input') && !target.closest('.dropdown-menu') && onDocumentSelect) {
                        onDocumentSelect(doc);
                      }
                    }}
                    style={{
                      cursor: isProcessing ? 'wait' : (onDocumentSelect ? 'pointer' : 'default'),
                      pointerEvents: isProcessing ? 'none' : 'auto'
                    }}
                  >
                    <td>
                      <div className="d-flex align-items-center">
                        {isProcessing ? (
                          <div className="spinner-border spinner-border-sm text-warning me-2" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          <i
                            className={`${getFileIcon(
                              doc.contentType,
                              doc.fileName
                            )} fs-4 me-2`}
                            style={{ color: '#495057' }}
                          ></i>
                        )}
                        <div className="flex-grow-1">
                          {editingFileName === doc.id ? (
                            <Input
                              type="text"
                              bsSize="sm"
                              value={editedFileName}
                              onChange={handleFileNameChange}
                              onBlur={() => handleFileNameBlur(doc)}
                              onKeyDown={(e) => handleFileNameKeyDown(e, doc)}
                              autoFocus
                              onClick={(e) => e.stopPropagation()}
                              style={{ maxWidth: '400px' }}
                            />
                          ) : (
                            <div
                              className="fw-medium d-flex align-items-center"
                              onClick={(e) => handleFileNameClick(doc, e)}
                              style={{
                                cursor: onFileNameUpdate && !doc.isDeleted ? 'pointer' : 'default',
                              }}
                            >
                              {doc.originalFileName}
                              {onFileNameUpdate && !doc.isDeleted && (
                                <i
                                  className="ri-edit-line ms-2 text-muted"
                                  style={{ fontSize: '14px' }}
                                  title={t('Clique para editar o nome do arquivo')}
                                ></i>
                              )}
                              {doc.originalFileName.includes('~') && (
                                <i
                                  className="ri-error-warning-line ms-2 text-warning"
                                  style={{ fontSize: '14px' }}
                                  title={t('Este arquivo pode ter um nome abreviado (8.3). Clique para corrigir.')}
                                ></i>
                              )}
                            </div>
                          )}
                          {doc.notes && (
                            <small className="text-muted">{doc.notes}</small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {isAdmin && onDocumentTypeChange && !doc.isDeleted ? (
                        <Input
                          type="select"
                          bsSize="sm"
                          value={normalizeDocumentType(doc.documentType)}
                          onChange={(e) =>
                            onDocumentTypeChange(doc, parseInt(e.target.value))
                          }
                          style={{ minWidth: '180px' }}
                        >
                          {getDocumentTypeOptions().map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      ) : (
                        getDocumentTypeLabel(doc.documentType, ownerType)
                      )}
                    </td>
                    {ownerType === DocumentOwnerType.Company && (() => {
                      const companyDoc = doc as CompanyDocumentDto;
                      const daysRemaining = calculateDaysRemaining(companyDoc.expirationDate);

                      return (
                        <>
                          {/* Sphere */}
                          <td>
                            <Badge color={getDocumentSphereColor(companyDoc.sphere)}>
                              {getDocumentSphereLabel(companyDoc.sphere)}
                            </Badge>
                          </td>
                          {/* Start Date */}
                          <td>
                            {companyDoc.startDate ? (
                              <small className="text-muted">
                                {formatDate(companyDoc.startDate)}
                              </small>
                            ) : (
                              <small className="text-muted">-</small>
                            )}
                          </td>
                          {/* Expiration Date */}
                          <td>
                            {companyDoc.expirationDate ? (
                              <small className="text-muted">
                                {formatDate(companyDoc.expirationDate)}
                              </small>
                            ) : (
                              <small className="text-muted">
                                <Badge color="secondary" className="badge-soft-secondary">
                                  Permanente
                                </Badge>
                              </small>
                            )}
                          </td>
                          {/* Days Remaining */}
                          <td>
                            {daysRemaining ? (
                              <Badge color={daysRemaining.color}>
                                {daysRemaining.label}
                              </Badge>
                            ) : (
                              <Badge color="secondary" className="badge-soft-secondary">
                                -
                              </Badge>
                            )}
                          </td>
                        </>
                      );
                    })()}
                    <td>
                      {doc.isDeleted ? (
                        <div>
                          <Badge color="dark">
                            <i className="ri-delete-bin-line me-1"></i>
                            {t('Excluído')}
                          </Badge>
                          {doc.canRestore === false && doc.restoreIssue && (
                            <div className="mt-1">
                              <Badge color="warning" className="d-flex align-items-start" style={{ fontSize: '11px' }}>
                                <i className="ri-alert-line me-1 flex-shrink-0" style={{ marginTop: '2px' }}></i>
                                <span className="text-start">{doc.restoreIssue}</span>
                              </Badge>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Badge color="success">
                          <i className="ri-checkbox-circle-line me-1"></i>
                          {t('Ativo')}
                        </Badge>
                      )}
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatFileSize(doc.fileSize)}
                      </small>
                    </td>
                    <td>
                      <small className="text-muted">
                        {formatDateTime(doc.createdAt)}
                      </small>
                    </td>
                    <td className="text-end">
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="button"
                          type="button"
                          className="btn btn-sm btn-soft-secondary"
                        >
                          <i className="ri-more-2-fill"></i>
                        </DropdownToggle>
                        <DropdownMenu end>
                          {!doc.isDeleted && (
                            <>
                              <DropdownItem
                                onClick={() => onPreview(doc)}
                              >
                                <i className="ri-eye-line me-2"></i>
                                {t('Visualizar')}
                              </DropdownItem>
                              <DropdownItem
                                onClick={() => onDownload(doc)}
                              >
                                <i className="ri-download-2-line me-2"></i>
                                {t('Baixar')}
                              </DropdownItem>
                              {isAdmin && onReplace && (
                                <>
                                  <DropdownItem divider />
                                  <DropdownItem
                                    onClick={() => onReplace(doc)}
                                  >
                                    <i className="ri-refresh-line me-2"></i>
                                    {t('Substituir')}
                                  </DropdownItem>
                                </>
                              )}
                              {isAdmin && onDelete && (
                                <>
                                  <DropdownItem divider />
                                  <DropdownItem
                                    onClick={() => handleDeleteClick(doc)}
                                    className="text-danger"
                                  >
                                    <i className="ri-delete-bin-line me-2"></i>
                                    {t('Excluir')}
                                  </DropdownItem>
                                </>
                              )}
                            </>
                          )}
                          {doc.isDeleted && isAdmin && onRestore && (
                            <>
                              <DropdownItem
                                onClick={() => doc.canRestore !== false && handleRestoreClick(doc)}
                                className={doc.canRestore === false ? "text-muted" : "text-success"}
                                disabled={doc.canRestore === false}
                              >
                                <i className="ri-arrow-go-back-line me-2"></i>
                                {t('Restaurar')}
                              </DropdownItem>
                              {doc.canRestore === false && doc.restoreIssue && (
                                <DropdownItem header className="text-wrap" style={{ maxWidth: '250px', fontSize: '11px' }}>
                                  <i className="ri-alert-line text-warning me-1"></i>
                                  {doc.restoreIssue}
                                </DropdownItem>
                              )}
                            </>
                          )}
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal.isOpen} toggle={() => setDeleteModal({ isOpen: false, document: null })}>
        <ModalHeader toggle={() => setDeleteModal({ isOpen: false, document: null })}>
          {t('Confirmar Exclusão')}
        </ModalHeader>
        <ModalBody>
          <p>
            {t('Tem certeza que deseja excluir o documento')}{' '}
            <strong>{deleteModal.document?.originalFileName}</strong>?
          </p>
          <FormGroup>
            <Label for="deleteReason">{t('Motivo da Exclusão')} (opcional)</Label>
            <Input
              type="textarea"
              id="deleteReason"
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              placeholder={t('Digite o motivo da exclusão...')}
              rows={3}
            />
          </FormGroup>
          <p className="text-muted small mb-0">
            <i className="ri-information-line me-1"></i>
            {t('O documento será movido para a pasta de exclusão e poderá ser restaurado.')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setDeleteModal({ isOpen: false, document: null })}
          >
            {t('Cancelar')}
          </Button>
          <Button color="danger" onClick={handleDeleteConfirm}>
            <i className="ri-delete-bin-line me-1"></i>
            {t('Excluir')}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal isOpen={restoreModal.isOpen} toggle={() => setRestoreModal({ isOpen: false, document: null })}>
        <ModalHeader toggle={() => setRestoreModal({ isOpen: false, document: null })}>
          {t('Confirmar Restauração')}
        </ModalHeader>
        <ModalBody>
          <p>
            {t('Tem certeza que deseja restaurar o documento')}{' '}
            <strong>{restoreModal.document?.originalFileName}</strong>?
          </p>
          <FormGroup>
            <Label for="restoreReason">{t('Motivo da Restauração')} (opcional)</Label>
            <Input
              type="textarea"
              id="restoreReason"
              value={restoreReason}
              onChange={(e) => setRestoreReason(e.target.value)}
              placeholder={t('Digite o motivo da restauração...')}
              rows={3}
            />
          </FormGroup>
          <p className="text-muted small mb-0">
            <i className="ri-information-line me-1"></i>
            {t('O documento será movido de volta para a pasta ativa.')}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setRestoreModal({ isOpen: false, document: null })}
          >
            {t('Cancelar')}
          </Button>
          <Button color="success" onClick={handleRestoreConfirm}>
            <i className="ri-arrow-go-back-line me-1"></i>
            {t('Restaurar')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default DocumentList;
